(() => {
  if (window.__sectionHeadingFitInitialized) {
    return;
  }
  window.__sectionHeadingFitInitialized = true;

  const getLineMetrics = (heading) => {
    const range = document.createRange();
    range.selectNodeContents(heading);

    const rawRects = Array.from(range.getClientRects()).filter((rect) => rect.width > 1 && rect.height > 1);
    const lines = [];

    rawRects.forEach((rect) => {
      const existingLine = lines.find((line) => Math.abs(line.top - rect.top) < 2);

      if (existingLine) {
        existingLine.left = Math.min(existingLine.left, rect.left);
        existingLine.right = Math.max(existingLine.right, rect.right);
        existingLine.top = Math.min(existingLine.top, rect.top);
        existingLine.bottom = Math.max(existingLine.bottom, rect.bottom);
        return;
      }

      lines.push({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
      });
    });

    if (typeof range.detach === 'function') {
      range.detach();
    }

    if (!lines.length) {
      return {
        lineCount: 1,
        widestLine: Math.ceil(heading.getBoundingClientRect().width),
      };
    }

    return {
      lineCount: lines.length,
      widestLine: Math.ceil(
        lines.reduce((widest, line) => Math.max(widest, line.right - line.left), 0)
      ),
    };
  };

  const fitHeading = (heading) => {
    if (!heading) return;

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const baseMin = Number(heading.dataset.fitMin) || 40;
    const minMobile = Number(heading.dataset.fitMinMobile) || baseMin;
    const minDesktop = Number(heading.dataset.fitMinDesktop) || baseMin;
    const min = isMobile ? minMobile : minDesktop;
    const baseMax = Number(heading.dataset.fitMax) || 1400;
    const maxMobile = Number(heading.dataset.fitMaxMobile) || baseMax;
    const maxDesktop = Number(heading.dataset.fitMaxDesktop) || baseMax;
    const max = isMobile ? maxMobile : maxDesktop;
    const nowrap = heading.dataset.fitNowrap === 'true';
    const mobileWrap = heading.dataset.fitMobileWrap !== 'false';
    const targetCharactersBase = Number(heading.dataset.fitTargetCharacters) || 0;
    const targetCharactersMobile = Number(heading.dataset.fitTargetCharactersMobile) || targetCharactersBase;
    const targetCharactersDesktop = Number(heading.dataset.fitTargetCharactersDesktop) || targetCharactersBase;
    const targetCharacters = isMobile ? targetCharactersMobile : targetCharactersDesktop;
    const availableWidth = heading.clientWidth;

    if (!availableWidth) return;

    const setWrappedLetterSpacing = (lineCount) => {
      if (!isMobile) {
        heading.style.letterSpacing = '1rem';
        return;
      }

      if (lineCount <= 1) {
        heading.style.letterSpacing = '1rem';
        return;
      }

      if (lineCount === 2) {
        heading.style.letterSpacing = '0.22rem';
        return;
      }

      if (lineCount === 3) {
        heading.style.letterSpacing = '0.12rem';
        return;
      }

      heading.style.letterSpacing = '0.06rem';
    };

    const setNoWrapLetterSpacing = (fontSize) => {
      const safeMax = Math.max(max, 1);
      const minSpacing = 0.12;
      const scaledSpacing = Math.max(minSpacing, Math.min(1, fontSize / safeMax));
      heading.style.letterSpacing = `${scaledSpacing.toFixed(3)}rem`;
    };

    if (!nowrap || (isMobile && mobileWrap)) {
      heading.style.fontSize = `${max}px`;
      setWrappedLetterSpacing(getLineMetrics(heading).lineCount);

      if (!isMobile && !nowrap) {
        heading.style.fontSize = `clamp(${min}px, 10vw, ${max}px)`;
        return;
      }

      const normalizedText = (heading.textContent || '').trim().replace(/\s+/g, ' ');
      const wordCount = normalizedText
        .split(/\s+/)
        .filter(Boolean).length;
      const preferredLineCount = targetCharacters > 0
        ? Math.max(1, Math.ceil(normalizedText.length / targetCharacters))
        : (wordCount > 1 ? 2 : 1);
      heading.style.fontSize = `${min}px`;
      setWrappedLetterSpacing(getLineMetrics(heading).lineCount);
      const minimumLineCount = getLineMetrics(heading).lineCount;
      const targetLineCount = Math.max(preferredLineCount, minimumLineCount);

      let low = min;
      let high = max;
      let best = min;

      while (high - low > 0.5) {
        const mid = (low + high) / 2;
        heading.style.fontSize = `${mid}px`;
        setWrappedLetterSpacing(getLineMetrics(heading).lineCount);

        const { lineCount, widestLine } = getLineMetrics(heading);

        if (lineCount <= targetLineCount && widestLine <= availableWidth) {
          best = mid;
          low = mid;
        } else {
          high = mid;
        }
      }

      heading.style.fontSize = `${Math.max(min, Math.min(max, best))}px`;
      setWrappedLetterSpacing(getLineMetrics(heading).lineCount);
      return;
    }

    let low = min;
    let high = max;
    let best = min;

    heading.style.fontSize = `${max}px`;
    setNoWrapLetterSpacing(max);

    while (high - low > 0.5) {
      const mid = (low + high) / 2;
      heading.style.fontSize = `${mid}px`;
      setNoWrapLetterSpacing(mid);

      if (heading.scrollWidth <= availableWidth) {
        best = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    const finalSize = Math.max(min, Math.min(max, best));
    heading.style.fontSize = `${finalSize}px`;
    setNoWrapLetterSpacing(finalSize);
  };

  const fitAll = (scope = document) => {
    scope.querySelectorAll('[data-section-heading-fit]').forEach((heading) => fitHeading(heading));
  };

  let resizeRaf = null;
  const onResize = () => {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => fitAll(document));
  };

  fitAll(document);
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => fitAll(document)).catch(() => {});
  }

  document.addEventListener('shopify:section:load', (event) => {
    fitAll(event.target);
  });
})();
